import { useEffect, useState } from "react";
import { Save, Check, X, Copy, Send, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { T } from "@/components/T";
import {
  adminGetRevenueCat, adminPutRevenueCat,
  adminGetFcm, adminPutFcm, adminTestFcm,
} from "@/lib/api";

const inp = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";
const btn = "mt-3 inline-flex items-center gap-1.5 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white disabled:opacity-50";

const Badge = ({ on }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${on ? "bg-green-500/10 text-green-600" : "bg-surface-alt text-ink-3"}`}>
    {on ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}{on ? <T>Live</T> : <T>Off</T>}
  </span>
);

function RevenueCatCard() {
  const [s, setS] = useState(null);
  const [auth, setAuth] = useState("");
  const [rows, setRows] = useState([]);

  const load = () => adminGetRevenueCat().then((d) => {
    setS(d);
    setAuth("");
    setRows(Object.entries(d.entitlement_tier_map || {}).map(([k, v]) => ({ k, v })));
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const map = {};
    rows.forEach(({ k, v }) => { if (k.trim() && v.trim()) map[k.trim()] = v.trim(); });
    const payload = { entitlement_tier_map: map };
    if (auth.trim()) payload.authorization_header = auth.trim();
    await adminPutRevenueCat(payload);
    toast.success("RevenueCat settings saved");
    load();
  };

  const copyUrl = () => {
    navigator.clipboard?.writeText(s?.webhook_url || "");
    toast.success("Webhook URL copied");
  };

  return (
    <div className="mb-6 border border-line bg-surface p-5" data-testid="revenuecat-card">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="font-medium text-ink"><T>RevenueCat Subscriptions</T></h2>
        <Badge on={s?.configured} />
      </div>
      <p className="mb-3 text-xs text-ink-3"><T>Paste the Authorization header value you set on your RevenueCat webhook. Changes go live immediately — no redeploy.</T></p>

      <label className="mb-1 block text-xs font-medium text-ink-3"><T>Webhook URL (set this in RevenueCat)</T></label>
      <div className="mb-4 flex items-center gap-2">
        <input readOnly value={s?.webhook_url || ""} data-testid="rc-webhook-url" className={`${inp} font-mono text-xs`} />
        <button onClick={copyUrl} data-testid="rc-copy-url" className="inline-flex items-center gap-1 border border-line px-3 py-2 text-xs text-ink hover:bg-surface-alt"><Copy className="h-3.5 w-3.5" /><T>Copy</T></button>
      </div>

      <label className="mb-1 block text-xs font-medium text-ink-3"><T>Authorization header secret</T></label>
      <input type="password" value={auth} onChange={(e) => setAuth(e.target.value)} placeholder={s?.configured ? "•••• secret on file — paste to replace ••••" : "Bearer my-secret-value"} data-testid="rc-auth-header" className={inp} />

      <label className="mt-4 mb-1 block text-xs font-medium text-ink-3"><T>Entitlement → tier mapping</T></label>
      <p className="mb-2 text-xs text-ink-3"><T>Map each RevenueCat entitlement identifier to an app tier (e.g. premium, family).</T></p>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`rc-map-row-${i}`}>
            <input value={r.k} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, k: e.target.value } : x))} placeholder="entitlement_id" className={`${inp} font-mono text-xs`} />
            <span className="text-ink-3">→</span>
            <input value={r.v} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, v: e.target.value } : x))} placeholder="tier" className={`${inp} font-mono text-xs`} />
            <button onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`rc-map-remove-${i}`} className="text-ink-3 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <button onClick={() => setRows([...rows, { k: "", v: "" }])} data-testid="rc-map-add" className="mt-2 inline-flex items-center gap-1 text-xs text-saffron hover:underline"><Plus className="h-3.5 w-3.5" /><T>Add mapping</T></button>

      <div>
        <button onClick={save} data-testid="rc-save" className={btn}><Save className="h-4 w-4" /><T>Save & go live</T></button>
      </div>
    </div>
  );
}

function FcmCard() {
  const [s, setS] = useState(null);
  const [projectId, setProjectId] = useState("");
  const [saJson, setSaJson] = useState("");
  const [testing, setTesting] = useState(false);

  const load = () => adminGetFcm().then((d) => {
    setS(d);
    setProjectId(d.project_id || "");
    setSaJson("");
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {};
    if (projectId.trim()) payload.project_id = projectId.trim();
    if (saJson.trim()) payload.service_account_json = saJson.trim();
    if (!saJson.trim() && !s?.configured) {
      toast.error("Paste your Firebase service account JSON");
      return;
    }
    try {
      await adminPutFcm(payload);
      toast.success("Push notifications are now live");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save FCM settings");
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await adminTestFcm({ title: "Dharmartha", body: "Push notifications are working." });
      toast.success(r.sent ? `Test push sent to ${r.sent} device(s)` : (r.configured ? "No devices registered for your account yet" : "FCM is not configured yet"));
    } catch {
      toast.error("Could not send test push");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="border border-line bg-surface p-5" data-testid="fcm-card">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="font-medium text-ink"><T>Firebase Push (FCM)</T></h2>
        <Badge on={s?.configured} />
      </div>
      <p className="mb-3 text-xs text-ink-3"><T>Paste your Firebase service account JSON to enable push notifications. Get it from Firebase Console → Project settings → Service accounts → Generate new private key.</T></p>

      {s?.client_email ? (
        <p className="mb-3 text-xs text-ink-3"><T>Current service account:</T> <span className="font-mono text-ink">{s.client_email}</span></p>
      ) : null}

      <label className="mb-1 block text-xs font-medium text-ink-3"><T>Project ID</T></label>
      <input value={projectId} onChange={(e) => setProjectId(e.target.value)} placeholder="my-firebase-project" data-testid="fcm-project-id" className={inp} />

      <label className="mt-3 mb-1 block text-xs font-medium text-ink-3"><T>Service account JSON</T></label>
      <textarea value={saJson} onChange={(e) => setSaJson(e.target.value)} rows={6} placeholder={s?.configured ? "•••• service account on file — paste to replace ••••" : '{ "type": "service_account", "project_id": "...", "private_key": "...", "client_email": "..." }'} data-testid="fcm-sa-json" className={`${inp} font-mono text-xs`} />

      <div className="flex flex-wrap items-center gap-3">
        <button onClick={save} data-testid="fcm-save" className={btn}><Save className="h-4 w-4" /><T>Save & go live</T></button>
        <button onClick={sendTest} disabled={testing || !s?.configured} data-testid="fcm-test" className="mt-3 inline-flex items-center gap-1.5 border border-line px-5 py-2.5 text-sm font-medium text-ink hover:bg-surface-alt disabled:opacity-50"><Send className="h-4 w-4" /><T>Send test push</T></button>
      </div>
    </div>
  );
}

export default function AdminIntegrations() {
  return (
    <div className="mx-auto max-w-3xl p-6" data-testid="admin-integrations">
      <h1 className="mb-1 text-2xl font-semibold text-ink"><T>Payments & Push</T></h1>
      <p className="mb-6 text-sm text-ink-3"><T>Connect RevenueCat subscriptions and Firebase push notifications. Paste your keys here to take these integrations live — no redeploy needed.</T></p>
      <RevenueCatCard />
      <FcmCard />
    </div>
  );
}
